import { Client } from "@stomp/stompjs";
import { whenever } from "@vueuse/core";
import fixWebmDuration from "fix-webm-duration";
import { computed, onMounted, onBeforeUnmount, ref, watch } from "vue";
import { useFileCache } from "../composables/file-cache";
import { devMode } from "@/config";
export const useMediaRecorder = (config) => {
    const mediaStream = config?.mediaStream || ref();
    const paused = config?.paused || ref(false);
    const mimeType = config?.mimeType || ref();
    console.log("mimeType" + mimeType.value);
    const lastResponseTime = ref();
    const lastRequestTime = ref();
    const TIMEOUT = 6000;
    const uuid = ref(crypto.randomUUID());
    const checkTimeout = () => {
        const req = lastRequestTime?.value ?? 0;
        const res = lastResponseTime?.value ?? 0;
        if (!errTimeout && !connectionError.value && req > 0 && req - res > TIMEOUT) {
            errTimeout = setTimeout(() => {
                if (devMode)
                    console.log("checkTimeout connectionError.value = true;");
                connectionError.value = true;
            }, TIMEOUT);
        }
    };
    const errInterval = setInterval(checkTimeout, TIMEOUT);
    const requestSent = () => {
        lastRequestTime.value = Date.now();
    };
    watch(lastResponseTime, () => {
        clearTimeout(errTimeout);
        errTimeout = undefined;
        connectionError.value = false;
    });
    let errTimeout = undefined;
    const wsURL = "wss://" + window.location.host + "/ws/media";
    const client = new Client({
        brokerURL: wsURL,
        onDisconnect: () => { },
        onWebSocketError: (_e) => {
            if (!connectionError.value && !errTimeout) {
                errTimeout = setTimeout(() => {
                    if (devMode)
                        console.log("onWebSocketError connectionError.value = true;");
                    connectionError.value = true;
                }, TIMEOUT);
            }
        },
        onConnect: () => {
            clearTimeout(errTimeout);
            errTimeout = undefined;
            connectionError.value = false;
            if (devMode)
                console.log("onConnect connectionError.value = false;");
            client.subscribe("/user/queue/put/reply", (message) => {
                if (/^\d+$/.test(message.body)) {
                    streamBuffer.value?.chunks.delete(parseInt(message.body));
                    lastResponseTime.value = Date.now();
                }
                else {
                    republishRequired.value = true;
                }
            });
            savingSubmitted.value = false;
            if (chunkSize.value > 0)
                republishRequired.value = true;
            client.subscribe("/user/queue/save/reply", (message) => {
                recordingSaved.value = message.body === "true";
                lastResponseTime.value = Date.now();
            });
        }
    });
    onBeforeUnmount(() => {
        clearInterval(errInterval);
        clearTimeout(errTimeout);
        client.deactivate();
    });
    onMounted(() => {
        client.activate();
    });
    const streamBuffer = ref({
        currentIndex: 0,
        currentSegment: 0,
        segmentDataReceived: 0,
        totalDataReceived: 0,
        chunks: new Map()
    });
    const connectionError = ref(false);
    const republishing = ref(false);
    const republishRequired = ref(false);
    const connected = computed(() => !!client?.connected);
    whenever(republishRequired, () => {
        if (!republishing.value) {
            setTimeout(attemptRecovery, 5000);
        }
    });
    const attemptRecovery = () => {
        if (devMode)
            console.log("attemptRecovery: " + connected.value);
        republishing.value = true;
        republishRequired.value = false;
        let err = false;
        let part = 0;
        const keys = Array.from(streamBuffer.value.chunks.keys());
        for (let idx = 0; idx < keys.length && !err && connected.value; idx++) {
            part = keys[idx];
            err = !sendChunk(part);
        }
        if (err) {
            setTimeout(attemptRecovery, 5000);
        }
        republishing.value = false;
    };
    const chunkSize = computed(() => streamBuffer.value.chunks.size);
    const chunkDataRemaining = computed(() => {
        return Array.from(streamBuffer.value.chunks.values()).reduce((total, value) => total + value.size, 0);
    });
    const recordingReady = computed(() => streamBuffer.value.chunks.size <= 0 && streamBuffer.value.totalDataReceived > 0);
    const uploadProgress = computed(() => {
        if (streamBuffer.value.totalDataReceived <= 0 || chunkDataRemaining.value >= streamBuffer.value.totalDataReceived)
            return 0;
        if (chunkDataRemaining.value <= 0)
            return 100;
        return Math.floor(((streamBuffer.value.totalDataReceived - chunkDataRemaining.value) / streamBuffer.value.totalDataReceived) * 100);
    });
    const isRecording = ref(false);
    const recordingSaved = ref(false);
    const savingSubmitted = ref(false);
    // const recorderConfig = reactive<RecorderConfig>(config);
    const recorderStream = ref();
    let mediaRecorder;
    // Local playback/upload data
    const segmentRecordings = ref([]);
    // Recorder buffer
    const fileCache = [];
    const refactorCache = [];
    // let chunks: Blob[][] = [];
    let segmentIncr = 0;
    let recordingSegment = 0;
    const recordedSeconds = ref(0);
    let totalTime = 0;
    const resetRecording = async () => {
        if (mediaRecorder)
            mediaRecorder.onstop = null;
        if (mediaRecorder)
            mediaRecorder.ondataavailable = null;
        stopRecording(true);
        segmentIncr = 0;
        recordingSegment = 0;
        savingSubmitted.value = false;
        mediaRecorder = undefined;
        paused.value = false;
        isRecording.value = false;
        recorderStream.value = undefined;
        console.log("recorderStream RESET");
        fileCache.splice(0, fileCache.length);
        refactorCache.splice(0, refactorCache.length);
        segmentRecordings.value = [];
        streamBuffer.value = {
            currentIndex: 0,
            currentSegment: 0,
            segmentDataReceived: 0,
            totalDataReceived: 0,
            chunks: new Map()
        };
        totalTime = 0;
        recordedSeconds.value = 0;
        uuid.value = crypto.randomUUID();
    };
    // const disconnect = () => client.deactivate();
    const stopRecording = async (deleteFiles) => {
        if (devMode)
            console.log("stopRecording: " + recordingSaved.value);
        if (mediaRecorder && mediaRecorder.state != "inactive") {
            mediaRecorder.stop();
        }
        if (deleteFiles) {
            fileCache.filter((f) => f != null).forEach((f) => f.remove());
            refactorCache.filter((f) => f != null).forEach((f) => f.remove());
        }
    };
    const startRecording = async () => {
        try {
            console.log("startRecording=>", !mediaRecorder);
            if (!mediaRecorder) {
                const recorder = await initializeRecorder();
                console.log("Recorder = ", recorder);
                if (recorder) {
                    recorder.start(1000);
                    mediaRecorder = recorder;
                }
            }
        }
        catch (e) {
            if (devMode)
                console.log(e);
            handleMediaError();
        }
    };
    watch(paused, async () => {
        if (!paused.value) {
            if (mediaRecorder != undefined) {
                if (mediaRecorder?.state == "paused") {
                    fileCache[segmentIncr - 1].acceptData();
                    mediaRecorder?.resume();
                }
                else if (mediaRecorder?.state == "inactive") {
                    mediaRecorder?.start(1000);
                }
            }
        }
        else {
            if (mediaRecorder?.state == "recording") {
                mediaRecorder?.pause();
            }
        }
    });
    const getRecording = (recordingSegment, currentSegment, duration, file) => {
        return {
            id: Date.now(),
            segment: recordingSegment,
            recorderSegment: currentSegment,
            file: file,
            duration: duration,
            offset: getOffset(recordingSegment)
        };
    };
    watch(mediaStream, async (n) => {
        if (n && mediaRecorder) {
            console.log(`mediaStream change : paused = ${paused.value}; mediaRecorder?.state = ${mediaRecorder?.state}`);
            if (mediaRecorder?.state != "inactive") {
                mediaRecorder.stop();
            }
            if (isRecording.value) {
                const recorder = await initializeRecorder();
                if (recorder) {
                    mediaRecorder = recorder;
                    mediaRecorder.start(1000);
                }
            }
            else {
                mediaRecorder = undefined;
            }
        }
    });
    // let startTime = 0;
    let initializingLock = false;
    const segmentTimes = [];
    const initializeRecorder = async () => {
        if (initializingLock || !mediaStream.value)
            return;
        initializingLock = true;
        try {
            // Scope the segment and timer to the recorder to avoid race conditions
            const segment = segmentIncr++;
            if (!fileCache[segment])
                fileCache[segment] = await useFileCache(uuid.value + "_" + segment);
            if (devMode)
                console.log(fileCache[segment]);
            let timerLock;
            let startTime = Date.now();
            segmentTimes[segment] = 0;
            const updateCurrentTime = () => {
                const now = Date.now();
                const elapsed = totalTime + (now - startTime);
                recordedSeconds.value = elapsed / 1000;
            };
            const startClock = () => {
                if (timerLock)
                    stopClock();
                startTime = Date.now();
                timerLock = setInterval(updateCurrentTime, 100);
            };
            const stopClock = () => {
                if (timerLock) {
                    clearInterval(timerLock);
                    timerLock = undefined;
                    if (devMode)
                        console.log(` StopClock #${segment} > time = ${segmentTimes[segment]} : + ${Date.now() - startTime}`);
                    segmentTimes[segment] += Date.now() - startTime;
                    totalTime += Date.now() - startTime;
                }
            };
            const recorder = new MediaRecorder(mediaStream.value, { mimeType: mimeType.value });
            // Handle streamed data chunks
            recorder.ondataavailable = async (event) => {
                if (recorder?.state != "recording") {
                    clearTimeout(storagewait);
                }
                if (recordingSaved.value)
                    return;
                const currentSegment = segment;
                if (event.data.size != 0) {
                    try {
                        const part = saveBlob(event.data, currentSegment);
                        if (!republishRequired.value)
                            sendChunk(part);
                    }
                    catch (err) {
                        if (devMode)
                            console.error(err);
                    }
                }
                if (recorder?.state == "inactive" || recorder?.state == undefined) {
                    // too late
                    return;
                }
                if (recorder?.state == "paused" && pausedCount < 2) {
                    waitForRecorder(pausedCount++ < 1 ? 1200 : 0, segment);
                }
            };
            recorder.onstart = recorder.onresume = () => {
                startClock();
                // check and re-disable audio - ios recorder not muted
                if (mediaStream.value &&
                    mediaStream.value.getAudioTracks().length > 0 &&
                    !mediaStream.value.getAudioTracks()[0].enabled) {
                    mediaStream.value.getAudioTracks()[0].enabled = true;
                    mediaStream.value.getAudioTracks()[0].enabled = false;
                }
                isRecording.value = true;
            };
            let pausedCount = 0;
            recorder.onpause = () => {
                if (devMode)
                    console.log("onPause");
                stopClock();
                pausedCount = 0;
                waitForRecorder(1200, segment);
                recorder?.requestData();
            };
            recorder.onstop = () => {
                if (isRecording.value) {
                    if (devMode)
                        console.log(`onStop ${isRecording.value} ${paused.value}`);
                    if (timerLock)
                        stopClock();
                    persistRecording(segment, paused.value);
                }
            };
            recorder.onerror = (e) => {
                if (devMode)
                    console.log(e);
                handleMediaError();
            };
            let storagewait;
            const waitForRecorder = (wait, segment) => {
                clearTimeout(storagewait);
                storagewait = setTimeout(() => persistRecording(segment, recorder?.state == "paused"), wait);
            };
            return recorder;
        }
        catch (e) {
            if (devMode)
                console.log(e);
        }
        finally {
            if (devMode)
                console.log("Inititialized");
            initializingLock = false;
        }
    };
    const persistRecording = async (segment, paused) => {
        await createRecording(segment);
        if (paused) {
            isRecording.value = false;
            fileCache[segment].acceptData();
        }
    };
    const cachedSize = computed(() => {
        const val = [...streamBuffer.value.chunks.values()].reduce((t, s) => t + (s.blob?.size ?? 0), 0);
        return val;
    });
    const getOffset = (segment) => {
        const val = segmentRecordings.value
            .filter((_s, index) => index < segment)
            .reduce((t, s) => t + s.duration, 0);
        return val;
    };
    const createRecording = async (currentSegment) => {
        try {
            const size = await fileCache[currentSegment].flush();
            if (size <= 0) {
                return;
            }
            if (recordingSegment > 0 &&
                segmentRecordings.value?.length >= recordingSegment - 1 &&
                segmentRecordings.value[recordingSegment - 1] &&
                segmentRecordings.value[recordingSegment - 1].recorderSegment === currentSegment) {
                recordingSegment = recordingSegment - 1;
            }
            segmentRecordings.value[recordingSegment] = getRecording(recordingSegment, currentSegment, segmentTimes[currentSegment] / 1000, fileCache[currentSegment]);
            if (mimeType.value && mimeType.value.indexOf("webm") >= 0 && currentSegment > 0) {
                if (currentSegment == 1) {
                    await doRefactorTiming(0, 0);
                }
                await doRefactorTiming(currentSegment, recordingSegment);
                segmentRecordings.value[recordingSegment].offset = getOffset(recordingSegment);
            }
        }
        catch (e0) {
            console.log(e0);
        }
        recordingSegment++;
    };
    const doRefactorTiming = async (currentSegment, recordingSegment) => {
        let actualTime = segmentTimes[currentSegment];
        const file = await fileCache[currentSegment].getFile();
        actualTime = (await getDuration(file)) * 1000;
        totalTime -= segmentTimes[currentSegment] - actualTime;
        segmentRecordings.value[recordingSegment].duration = actualTime / 1000;
        const updatedFile = await fixWebmDuration(file, actualTime, { logger: false });
        if (devMode)
            console.log(`${recordingSegment} -> Originally ${segmentTimes[currentSegment]} detected ${actualTime} : ${file.size} -> ${updatedFile.size} bytes`);
        segmentTimes[currentSegment] = actualTime;
        const cache = (refactorCache[currentSegment] =
            refactorCache[currentSegment] ?? (await useFileCache(uuid.value + "_" + currentSegment + "refactored")));
        // if ([...streamBuffer.value.chunks.values()].find((c) => c.segment == currentSegment && !c.blob)) {
        // } else {
        //   streamBuffer.value.totalDataReceived = updatedFile.size;
        // }
        await cache.overwrite(updatedFile);
        if (segmentRecordings.value[recordingSegment].url) {
            console.log(`REVOKE ${segmentRecordings.value[recordingSegment].url}`);
            window.URL.revokeObjectURL(segmentRecordings.value[recordingSegment].url);
        }
        segmentRecordings.value[recordingSegment].url = window.URL.createObjectURL(await cache.getFile());
        console.log(`NEW URL ${segmentRecordings.value[recordingSegment].url}`);
    };
    const handleMediaError = () => {
        // recorderStatus.value = RecorderStatus.ERROR;
    };
    const saveRecording = async () => {
        if (savingSubmitted.value)
            return;
        savingSubmitted.value = true;
        requestSent();
        client.publish({
            destination: "/api/websocket/media/save",
            body: uuid.value,
            headers: {
                segments: streamBuffer.value.currentSegment.toString()
            }
        });
    };
    const saveBlob = (blob, currentSegment) => {
        // This need to be of type Uint8Array
        // setting contstreamBufferent-type header is not mandatory, however a good practice
        if (streamBuffer.value.currentSegment != currentSegment) {
            streamBuffer.value.segmentDataReceived = 0;
        }
        const index = streamBuffer.value.currentIndex++;
        const offset = streamBuffer.value.segmentDataReceived;
        streamBuffer.value.segmentDataReceived += blob.size;
        streamBuffer.value.totalDataReceived += blob.size;
        if (devMode)
            console.log(`%csaveBlob ${segmentTimes[currentSegment]} : segment ${currentSegment} part ${index} size ${blob.size} offset ${offset}`, "color:blue; font-weight:bold;");
        streamBuffer.value.chunks.set(index, {
            part: index,
            segment: currentSegment,
            size: blob.size,
            offset: offset,
            blob: cachedSize.value < 5 * 1024 * 1024 ? blob : undefined
        });
        streamBuffer.value.currentSegment = currentSegment;
        fileCache[currentSegment].writeChunk({ data: blob, index, offset });
        return index;
    };
    const sendChunk = async (part) => {
        try {
            if (!connected.value) {
                republishRequired.value = true;
                return false;
            }
            const chunk = streamBuffer.value.chunks.get(part);
            if (chunk) {
                const blob = chunk.blob ?? (await fileCache[chunk.segment].readChunk(chunk.offset, chunk.size));
                const data = await readBlob(blob);
                // console.log(`PUT DATA ${data.length} ${chunk}`);
                requestSent();
                client.publish({
                    destination: "/api/websocket/media/put",
                    binaryBody: data,
                    headers: {
                        "content-type": "application/octet-stream",
                        part: part.toString(),
                        segment: chunk.segment.toString(),
                        offset: chunk.offset.toString(),
                        uuid: uuid.value ?? ""
                    }
                });
                // console.log(`segment ${chunk.segment}; part: ${part}; size: ${data.length}; offset: ${chunk.offset}`);
                return true;
            }
        }
        catch (error) {
            // Failure
            if (devMode)
                console.log(error);
            if (devMode)
                console.log("FAILED CHUNK #", part);
            republishRequired.value = true;
            return false;
        }
        return false;
    };
    const getDuration = async (blob) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = async () => {
                try {
                    const ctx = new AudioContext();
                    const audioArrayBuffer = reader.result;
                    if (audioArrayBuffer instanceof ArrayBuffer) {
                        ctx.decodeAudioData(audioArrayBuffer, (data) => {
                            const duration = data.duration;
                            resolve(duration);
                        }, (error) => {
                            reject(error);
                        });
                    }
                    else {
                        reject("invalid");
                    }
                }
                catch (e) {
                    if (devMode)
                        console.log(e);
                }
            };
            reader.readAsArrayBuffer(blob);
        });
    };
    const readBlob = async (blob) => {
        const buffer = await new Response(blob).arrayBuffer();
        return new Uint8Array(buffer);
    };
    return {
        mediaStream,
        mediaRecorder,
        paused,
        mimeType,
        isRecording,
        recordedSeconds,
        segmentRecordings,
        resetRecording,
        startRecording,
        saveRecording,
        stopRecording,
        connectionError,
        republishRequired,
        savingSubmitted,
        recordingReady,
        recordingSaved,
        chunkSize,
        uuid,
        streamBuffer,
        uploadProgress
    };
};
